import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  IconButton,
  Collapse,
  Chip,
  Grid,
  InputAdornment,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Create as CreateIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  PictureAsPdf as PdfIcon,
  FilterListOff as FilterListOffIcon,
} from '@mui/icons-material';
import { auditLogsAPI } from '../../../services/api';
import { AuditLog, AuditLogFilter, AuditLogPagination } from '../../../types/auditLog';
import { getModuleTableTheme, modernCellStyles } from '../../../theme/tableTheme';
import useAlert from '../../../hooks/useAlert';
import { extractErrorMessage } from '../../../utils/errorHandler';
import AlertDialog from '../../../components/AlertDialog';
import ColumnFilter from '../../../components/ColumnFilter';
import { useColumnFilters } from '../../../hooks/useColumnFilters';

const ADMIN_COLOR = '#F44336';

// `date` -> start_date/end_date are sent as YYYY-MM-DD; the backend's
// datetime parser accepts that. We apply the T00/T23 inclusivity transform
// inside fetchAuditLogs so the schema can stay declarative.
const AUDIT_FILTER_SCHEMA = {
  date:        { type: 'dateRange', paramFrom: 'start_date',  paramTo: 'end_date' },
  user_email:  { type: 'text',      param: 'user_email' },
  action:      { type: 'select',    param: 'action' },
  entity_type: { type: 'select',    param: 'entity_type' },
  entity_id:   { type: 'number',    param: 'entity_id', transform: (v: string) => Number(v) },
  ip_address:  { type: 'text',      param: 'ip_address' },
} as const;

// Row component with expandable details
const AuditLogRow: React.FC<{ log: AuditLog }> = ({ log }) => {
  const [open, setOpen] = useState(false);

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'CREATE':
        return <CreateIcon fontSize="small" />;
      case 'UPDATE':
        return <EditIcon fontSize="small" />;
      case 'DELETE':
        return <DeleteIcon fontSize="small" />;
      case 'LOGIN':
        return <LoginIcon fontSize="small" />;
      case 'LOGOUT':
        return <LogoutIcon fontSize="small" />;
      case 'PDF_DOWNLOAD':
        return <PdfIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const getActionColor = (action: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
    switch (action) {
      case 'CREATE':
        return 'success';
      case 'UPDATE':
        return 'warning';
      case 'DELETE':
        return 'error';
      case 'LOGIN':
      case 'LOGOUT':
        return 'info';
      case 'PDF_DOWNLOAD':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const renderDataDiff = (oldData: Record<string, any> | null, newData: Record<string, any> | null) => {
    if (!oldData && !newData) return null;

    const allKeys = new Set([
      ...Object.keys(oldData || {}),
      ...Object.keys(newData || {}),
    ]);

    return (
      <Box sx={{ mt: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          Détails des modifications:
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Champ</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Ancienne valeur</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Nouvelle valeur</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from(allKeys).map((key) => {
              const oldValue = oldData?.[key];
              const newValue = newData?.[key];
              const hasChanged = JSON.stringify(oldValue) !== JSON.stringify(newValue);

              return (
                <TableRow key={key} sx={{ backgroundColor: hasChanged ? 'rgba(255, 193, 7, 0.1)' : 'inherit' }}>
                  <TableCell>{key}</TableCell>
                  <TableCell sx={{ color: hasChanged ? 'error.main' : 'inherit' }}>
                    {oldValue !== undefined ? JSON.stringify(oldValue) : '-'}
                  </TableCell>
                  <TableCell sx={{ color: hasChanged ? 'success.main' : 'inherit' }}>
                    {newValue !== undefined ? JSON.stringify(newValue) : '-'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    );
  };

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={modernCellStyles}>{formatDate(log.created_at)}</TableCell>
        <TableCell sx={modernCellStyles}>
          {log.user_name || log.user_email ? (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                {log.user_name || log.user_email}
              </Typography>
              {log.user_name && log.user_email && (
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                  {log.user_email}
                </Typography>
              )}
            </Box>
          ) : (
            'Système'
          )}
        </TableCell>
        <TableCell sx={modernCellStyles}>
          <Chip
            icon={getActionIcon(log.action) || undefined}
            label={log.action}
            color={getActionColor(log.action)}
            size="small"
          />
        </TableCell>
        <TableCell sx={modernCellStyles}>{log.entity_type}</TableCell>
        <TableCell sx={modernCellStyles}>{log.entity_id || '-'}</TableCell>
        <TableCell sx={modernCellStyles}>{log.ip_address || '-'}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              {log.description && (
                <Typography variant="body2" gutterBottom>
                  <strong>Description:</strong> {log.description}
                </Typography>
              )}
              {renderDataDiff(log.old_data, log.new_data)}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const AuditLogs: React.FC = () => {
  const [data, setData] = useState<AuditLogPagination | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [actions, setActions] = useState<string[]>([]);
  const [entityTypes, setEntityTypes] = useState<string[]>([]);
  const { alertState, showError, hideAlert } = useAlert();

  const { filters: colFilters, update, reset, hasActive, toQueryParams } = useColumnFilters(
    AUDIT_FILTER_SCHEMA,
    { onChange: () => setPage(0) }
  );

  useEffect(() => {
    fetchAuditLogs();
    fetchFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, colFilters, appliedSearch]);

  const fetchAuditLogs = async () => {
    setLoading(true);
    try {
      const raw = toQueryParams() as Record<string, any>;
      // Re-attach inclusive time bounds so end_date covers the full day.
      const filters: AuditLogFilter = {
        ...raw,
        start_date: raw.start_date ? `${raw.start_date}T00:00:00` : undefined,
        end_date: raw.end_date ? `${raw.end_date}T23:59:59` : undefined,
        search: appliedSearch || undefined,
      };
      const result = await auditLogsAPI.getAll(page + 1, rowsPerPage, filters);
      setData(result);
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const [actionsData, entityTypesData] = await Promise.all([
        auditLogsAPI.getActions(),
        auditLogsAPI.getEntityTypes(),
      ]);
      setActions(actionsData);
      setEntityTypes(entityTypesData);
    } catch (error: any) {
      console.error('Error fetching filter options:', error);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    setAppliedSearch(searchTerm);
    setPage(0);
  };

  const handleClearAll = () => {
    setSearchTerm('');
    setAppliedSearch('');
    reset();
    setPage(0);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Box>
      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
      />

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          Journal d'Audit
        </Typography>

        {/* Filters */}
        <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
          <Grid item xs={12} sm={8} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Rechercher"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleSearch} size="small">
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item>
            {(hasActive() || appliedSearch || searchTerm) && (
              <Button
                size="small"
                startIcon={<FilterListOffIcon />}
                onClick={handleClearAll}
                sx={{ color: 'text.secondary' }}
              >
                Effacer les filtres
              </Button>
            )}
          </Grid>
        </Grid>

        {/* Table */}
        <TableContainer sx={getModuleTableTheme('administration')}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 50 }} />
                <TableCell sx={modernCellStyles}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Date/Heure
                    <ColumnFilter type="dateRange" value={colFilters.date} onChange={(v) => update('date', v)} label="date" color={ADMIN_COLOR} />
                  </Box>
                </TableCell>
                <TableCell sx={modernCellStyles}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Utilisateur
                    <ColumnFilter type="text" value={colFilters.user_email} onChange={(v) => update('user_email', v)} label="email utilisateur" color={ADMIN_COLOR} />
                  </Box>
                </TableCell>
                <TableCell sx={modernCellStyles}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Action
                    <ColumnFilter
                      type="select"
                      value={colFilters.action}
                      onChange={(v) => update('action', v)}
                      label="action"
                      color={ADMIN_COLOR}
                      options={actions.map((a) => ({ value: a, label: a }))}
                    />
                  </Box>
                </TableCell>
                <TableCell sx={modernCellStyles}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Type
                    <ColumnFilter
                      type="select"
                      value={colFilters.entity_type}
                      onChange={(v) => update('entity_type', v)}
                      label="type d'entité"
                      color={ADMIN_COLOR}
                      options={entityTypes.map((t) => ({ value: t, label: t }))}
                    />
                  </Box>
                </TableCell>
                <TableCell sx={modernCellStyles}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    ID
                    <ColumnFilter type="number" value={colFilters.entity_id} onChange={(v) => update('entity_id', v)} label="entity ID" color={ADMIN_COLOR} />
                  </Box>
                </TableCell>
                <TableCell sx={modernCellStyles}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    IP
                    <ColumnFilter type="text" value={colFilters.ip_address} onChange={(v) => update('ip_address', v)} label="adresse IP" color={ADMIN_COLOR} />
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Chargement...
                  </TableCell>
                </TableRow>
              ) : data?.items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Aucun enregistrement trouvé
                  </TableCell>
                </TableRow>
              ) : (
                data?.items.map((log) => <AuditLogRow key={log.id} log={log} />)
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={data?.total || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Lignes par page:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`
          }
        />
      </Paper>
    </Box>
  );
};

export default AuditLogs;
